import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import invoiceService from '../services/invoiceService';
import Button from '../components/Button';
import Input from '../components/Input';
import './TechnicianForm.css';

const emptyItem = () => ({ description: '', quantity: 1, unitPrice: 0, amount: 0 });
const emptyService = () => ({ description: '', amount: 0 });

const InvoiceEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    discount: 0,
    isGstApplied: true,
    gstRate: 18,
    notes: '',
    termsAndConditions: '',
    customer: {
      name: '',
      phone: '',
      email: '',
      gst: '',
      address: { street: '', city: '', state: '', zipCode: '' },
    },
    items: [emptyItem()],
    services: [emptyService()],
  });

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await invoiceService.getById(id);
      const inv = response.data || response;
      setFormData({
        discount: inv.discount ?? 0,
        isGstApplied: inv.isGstApplied !== false,
        gstRate: inv.gstRate != null ? Number(inv.gstRate) : 18,
        notes: inv.notes ?? '',
        termsAndConditions: inv.termsAndConditions ?? '',
        customer: {
          name: inv.customer?.name ?? '',
          phone: inv.customer?.phone ?? '',
          email: inv.customer?.email ?? '',
          gst: inv.customer?.gst ?? '',
          address: {
            street: inv.customer?.address?.street ?? '',
            city: inv.customer?.address?.city ?? '',
            state: inv.customer?.address?.state ?? '',
            zipCode: inv.customer?.address?.zipCode ?? '',
          },
        },
        items:
          inv.items?.length > 0
            ? inv.items.map((i) => ({
                description: i.description ?? '',
                quantity: i.quantity ?? 0,
                unitPrice: i.unitPrice ?? 0,
                amount: i.amount ?? 0,
              }))
            : [emptyItem()],
        services:
          inv.services?.length > 0
            ? inv.services.map((s) => ({
                description: s.description ?? '',
                amount: s.amount ?? 0,
              }))
            : [emptyService()],
      });
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCustomerChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      customer: { ...prev.customer, [field]: value },
    }));
  };

  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      customer: {
        ...prev.customer,
        address: { ...prev.customer.address, [field]: value },
      },
    }));
  };

  const updateItem = (index, field, value) => {
    const num = field === 'quantity' || field === 'unitPrice' || field === 'amount' ? Number(value) || 0 : value;
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: num };
      if (field === 'quantity' || field === 'unitPrice') {
        items[index].amount = items[index].quantity * items[index].unitPrice;
      }
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setFormData((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : [emptyItem()],
    }));
  };

  const updateService = (index, field, value) => {
    const num = field === 'amount' ? Number(value) || 0 : value;
    setFormData((prev) => {
      const services = [...prev.services];
      services[index] = { ...services[index], [field]: num };
      return { ...prev, services };
    });
  };

  const addService = () => {
    setFormData((prev) => ({ ...prev, services: [...prev.services, emptyService()] }));
  };

  const removeService = (index) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.length > 1 ? prev.services.filter((_, i) => i !== index) : [emptyService()],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        discount: Number(formData.discount) || 0,
        isGstApplied: formData.isGstApplied,
        gstRate: Number(formData.gstRate) || 0,
        notes: formData.notes || undefined,
        termsAndConditions: formData.termsAndConditions || undefined,
        customer: {
          name: formData.customer.name,
          phone: formData.customer.phone,
          email: formData.customer.email || undefined,
          gst: formData.customer.gst || undefined,
          address: formData.customer.address,
        },
        items: formData.items
          .filter((i) => i.description.trim() || i.quantity > 0 || i.unitPrice > 0)
          .map((i) => ({
            description: i.description,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            amount: i.quantity * i.unitPrice,
          })),
        services: formData.services
          .filter((s) => s.description.trim() || s.amount > 0)
          .map((s) => ({ description: s.description, amount: s.amount })),
      };
      const response = await invoiceService.update(id, payload);
      const data = response.data || response;
      toast.success('Invoice updated successfully');
      navigate(`/invoices/${data._id || id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <button type="button" onClick={() => navigate(`/invoices/${id}`)} className="back-button">
          <FiArrowLeft /> Back to Invoice
        </button>
        <h1 className="form-title">Edit Invoice</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Tax &amp; Amounts</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Apply GST</label>
              <select
                className="input-field"
                value={formData.isGstApplied ? 'yes' : 'no'}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, isGstApplied: e.target.value === 'yes' }))
                }
              >
                <option value="yes">Yes</option>
                <option value="no">No GST</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Tax / GST %</label>
              <input
                type="number"
                className="input-field"
                min="0"
                max="100"
                step="0.01"
                value={formData.gstRate}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, gstRate: e.target.value }))
                }
                disabled={!formData.isGstApplied}
              />
            </div>
            <div className="input-group">
              <label className="input-label">Discount (₹)</label>
              <input
                type="number"
                className="input-field"
                min="0"
                step="0.01"
                value={formData.discount}
                onChange={(e) => setFormData((p) => ({ ...p, discount: e.target.value }))}
              />
            </div>
          </div>
          <div className="input-group" style={{ marginTop: '1rem' }}>
            <label className="input-label">Notes</label>
            <textarea
              className="input-field"
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
          <div className="input-group" style={{ marginTop: '1rem' }}>
            <label className="input-label">Terms &amp; Conditions</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.termsAndConditions}
              onChange={(e) => setFormData((p) => ({ ...p, termsAndConditions: e.target.value }))}
              placeholder="Payment terms, etc."
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Customer</h3>
          <div className="form-grid">
            <Input
              label="Name"
              name="customerName"
              value={formData.customer.name}
              onChange={(e) => handleCustomerChange('name', e.target.value)}
              required
            />
            <Input
              label="Phone"
              name="customerPhone"
              value={formData.customer.phone}
              onChange={(e) => handleCustomerChange('phone', e.target.value)}
              required
            />
            <Input
              label="Email"
              name="customerEmail"
              type="email"
              value={formData.customer.email}
              onChange={(e) => handleCustomerChange('email', e.target.value)}
            />
            <Input
              label="GST Number"
              name="customerGst"
              value={formData.customer.gst}
              onChange={(e) => handleCustomerChange('gst', e.target.value)}
            />
            <Input
              label="Street"
              name="street"
              value={formData.customer.address.street}
              onChange={(e) => handleAddressChange('street', e.target.value)}
            />
            <Input
              label="City"
              name="city"
              value={formData.customer.address.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
            />
            <Input
              label="State"
              name="state"
              value={formData.customer.address.state}
              onChange={(e) => handleAddressChange('state', e.target.value)}
            />
            <Input
              label="Zip Code"
              name="zipCode"
              value={formData.customer.address.zipCode}
              onChange={(e) => handleAddressChange('zipCode', e.target.value)}
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Line Items</h3>
          {formData.items.map((item, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 100px 100px auto',
                gap: '0.75rem',
                alignItems: 'end',
                marginBottom: '0.75rem',
              }}
            >
              <Input
                label={idx === 0 ? 'Description' : ''}
                value={item.description}
                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                placeholder="Item description"
              />
              <Input
                label={idx === 0 ? 'Qty' : ''}
                type="number"
                min="0"
                value={item.quantity}
                onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
              />
              <Input
                label={idx === 0 ? 'Unit Price' : ''}
                type="number"
                min="0"
                step="0.01"
                value={item.unitPrice}
                onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)}
              />
              <div className="input-group">
                {idx === 0 && <label className="input-label">Amount</label>}
                <input
                  type="text"
                  className="input-field"
                  readOnly
                  value={item.quantity * item.unitPrice}
                  style={{ background: 'var(--gray-50)' }}
                />
              </div>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="back-button"
                style={{ marginBottom: 0 }}
                aria-label="Remove item"
              >
                <FiTrash2 />
              </button>
            </div>
          ))}
          <button type="button" onClick={addItem} className="back-button" style={{ marginTop: '0.5rem' }}>
            <FiPlus /> Add Item
          </button>
        </div>

        <div className="form-section">
          <h3 className="section-title">Services</h3>
          {formData.services.map((service, idx) => (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px auto',
                gap: '0.75rem',
                alignItems: 'end',
                marginBottom: '0.75rem',
              }}
            >
              <Input
                label={idx === 0 ? 'Description' : ''}
                value={service.description}
                onChange={(e) => updateService(idx, 'description', e.target.value)}
                placeholder="Service description"
              />
              <Input
                label={idx === 0 ? 'Amount (₹)' : ''}
                type="number"
                min="0"
                step="0.01"
                value={service.amount}
                onChange={(e) => updateService(idx, 'amount', e.target.value)}
              />
              <button
                type="button"
                onClick={() => removeService(idx)}
                className="back-button"
                style={{ marginBottom: 0 }}
                aria-label="Remove service"
              >
                <FiTrash2 />
              </button>
            </div>
          ))}
          <button type="button" onClick={addService} className="back-button" style={{ marginTop: '0.5rem' }}>
            <FiPlus /> Add Service
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <Button type="submit" variant="primary" icon={<FiSave />} loading={loading}>
            Save Changes
          </Button>
          <button
            type="button"
            onClick={() => navigate(`/invoices/${id}`)}
            className="back-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceEdit;
